import { FileSignature, HandCoins, Receipt, Shield, type LucideIcon } from 'lucide-react';

import { parseSubjectParam } from '../dossier/subjectParam';

/**
 * insuranceSteps.ts — the four areas one year of cover is recorded in.
 *
 * `advanceSteps.ts` FOR A DIFFERENT SUBJECT, AND DELIBERATELY ITS MIRROR. The policy dossier is the
 * contract dossier's shape over one `insurance_contracts` junction: an area is a question, the
 * manifest is the preflight, and one function builds every path so a rename cannot half-land. What
 * it is NOT is a seventh setup area — `SetupArea` stays the closed union of six it has always been,
 * and nothing here widens it. Where the two vocabularies meet they meet as lookups: `labelForArea`
 * consults the area labels after its own, and a merge-field pre-flight routes through
 * `insuranceDeepLink`.
 *
 * THE ORDER IS THE YEAR'S OWN. Cover is bought before it is paid for, it is paid for before anybody
 * asks who financed it, and the instrument that states the financing is signed last — so an admin
 * walking these four areas walks the year in the order it happened.
 *
 * FOUR AREAS AND NOT SIX STEPS. The side sheet this replaces asked its six questions in a fixed
 * march (policy, premium, discount, loan, deposit, instalment) with no way back to a step that had
 * been passed; the discount is a row of the premium area here and the deposit and the weekly
 * recovery are rows of the advance area, because they are all facts about the same two records.
 *
 * PURE, AND REACT-FREE but for the icon type: vitest runs node-env over `.ts`, and the icons are
 * values the rail draws rather than anything this module executes.
 */
export type InsuranceArea = 'policy' | 'premium' | 'advance' | 'agreement';

export interface InsuranceAreaConfig {
    key: InsuranceArea;
    label: string;
    /** The question the area answers, in the admin's own words. */
    question: string;
    icon: LucideIcon;
}

export const INSURANCE_AREAS: InsuranceAreaConfig[] = [
    {
        key: 'policy',
        label: 'Policy',
        question: 'What is covered, for how much, and over what period?',
        icon: Shield,
    },
    {
        key: 'premium',
        label: 'Premium',
        question: 'Who paid the insurer, and did anything come back?',
        icon: Receipt,
    },
    {
        key: 'advance',
        label: 'Advance',
        question: 'Did Primelane finance the premium, and how is it recovered?',
        icon: HandCoins,
    },
    {
        key: 'agreement',
        label: 'Agreement',
        question: 'Is the instrument executed by both parties?',
        icon: FileSignature,
    },
];

export const INSURANCE_AREA_KEYS: InsuranceArea[] = INSURANCE_AREAS.map((area) => area.key);

export const INSURANCE_AREA_LABELS: Record<InsuranceArea, string> = INSURANCE_AREAS.reduce(
    (labels, area) => ({ ...labels, [area.key]: area.label }),
    {} as Record<InsuranceArea, string>,
);

/**
 * The area a URL fragment names, or null.
 *
 * NULL IS "NO AREA WAS NAMED", exactly as it is in `resolveArea` and `resolveAdvanceArea`: an
 * unknown value, a hand-edited path or a missing segment all land on the same answer, and the
 * page's landing rule takes it from there rather than pinning the admin to the first area.
 */
export function resolveInsuranceArea(value: string | null | undefined): InsuranceArea | null {
    if (!value) return null;
    const key = value.toLowerCase();
    return INSURANCE_AREA_KEYS.includes(key as InsuranceArea) ? (key as InsuranceArea) : null;
}

/**
 * The `:policyId` segment as a subject — a policy id, the literal `new`, or nothing.
 *
 * THE RULE IS `subjectParam`'s AND THE NAME IS THIS DOSSIER'S, as `parseLoanParam` is the advance's.
 * `insurances.id` is a bigserial and the segment is hand-editable, so a `Number()` on this path
 * produces the `NaN` filter that parser exists to refuse.
 */
export function parsePolicyParam(value: string | null | undefined): number | 'new' | null {
    return parseSubjectParam(value);
}

/**
 * The path an insurance area lives at. ONE PLACE BUILDS IT, so a rename cannot half-land.
 *
 * `policy` IS THE SEGMENT AS IT IS WRITTEN, never a parse: `'new'` travels as the word the route
 * matches on, and an id travels as its digits. A bare area-less path is the landing route, which
 * the page replaces with a real area the moment it can resolve one.
 */
export function insuranceAreaPath(
    contractId: number,
    policy: number | 'new',
    area: InsuranceArea | null,
): string {
    const base = `/dashboard/contracts/${contractId}/insurance/${policy}`;
    return area ? `${base}/${area}` : base;
}
